

/**
 * Horizontal Scroll Component
 * Pins section and converts vertical scroll to horizontal scroll
 * Releases when all cards have been scrolled through
 *
 * Usage: Add x-data="horizontalScroll" to the wrapper element
 */
(function () {
    function register(Alpine) {
        if (Alpine.__horizontalScrollRegistered) return;
        Alpine.__horizontalScrollRegistered = true;

        Alpine.data('horizontalScroll', () => ({
            wrapper: null,
            container: null,
            cards: null,
            scrollTriggerInstance: null,
            initialized: false,
            // Pinning the section and scrubbing the row sideways is exactly the kind of movement
            // "prefers-reduced-motion" asks us not to do, so the row stays a plain scrollable list.
            reduced: window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false,

            init() {
                this.wrapper = this.$el;
                this.container = this.wrapper.querySelector('.hscroll-track');
                this.cards = this.wrapper.querySelector('.hscroll-cards');

                if (!this.container || !this.cards) {
                    return;
                }

                if (this.reduced) {
                    this.wrapper.classList.add('hscroll-static');
                    return;
                }

                // Use multiple fallbacks to ensure setup runs
                if (document.readyState === 'complete') {
                    setTimeout(() => this.setupScrollTrigger(), 300);
                } else {
                    window.addEventListener('load', () => {
                        setTimeout(() => this.setupScrollTrigger(), 300);
                    });
                }
            },

            setupScrollTrigger() {
                if (this.initialized) return;

                // gsap + ScrollTrigger come from the shared lib/gsap.min.js chunk (see lib/gsap.js),
                // which is loaded async. Alpine can init before that script has run, so they are
                // resolved here instead: this runs after "load", which waits for async scripts.
                const gsap = window.gsap;
                const ScrollTrigger = window.ScrollTrigger;
                if (!gsap || !ScrollTrigger) {
                    console.warn('[HorizontalScroll] gsap not available - include Resources/Public/js/lib/gsap.min.js before this script');
                    this.wrapper.classList.add('hscroll-static');
                    return;
                }

                // Calculate how far we need to scroll horizontally
                const scrollWidth = this.cards.scrollWidth - this.container.offsetWidth;

                if (scrollWidth <= 0) {
                    // No horizontal scroll needed
                    return;
                }

                // Get header height - create a temp element to compute the actual pixel value
                const tempEl = document.createElement('div');
                tempEl.style.height = 'var(--header-height, 0px)';
                tempEl.style.position = 'absolute';
                tempEl.style.visibility = 'hidden';
                document.body.appendChild(tempEl);
                const headerHeightPx = tempEl.offsetHeight;
                document.body.removeChild(tempEl);


                // Create the scroll trigger animation
                this.scrollTriggerInstance = gsap.to(this.cards, {
                    x: -scrollWidth,
                    ease: 'none',
                    scrollTrigger: {
                        trigger: this.wrapper,
                        start: `top top+=${headerHeightPx}`,
                        end: `+=${scrollWidth}`,
                        pin: true,
                        pinSpacing: true,
                        scrub: 1,
                        anticipatePin: 1,
                        invalidateOnRefresh: true,
                    }
                });

                this.initialized = true;
            },

            destroy() {
                if (this.scrollTriggerInstance && this.scrollTriggerInstance.scrollTrigger) {
                    this.scrollTriggerInstance.scrollTrigger.kill();
                }
                this.initialized = false;
            }
        }));
    }

    // Register now if Alpine exists; otherwise wait for 'alpine:init'
    if (window.Alpine) {
        register(window.Alpine);
    } else {
        document.addEventListener('alpine:init', () => register(window.Alpine));
    }

    // Refresh ScrollTrigger after full page load
    window.addEventListener('load', () => {
        setTimeout(() => {
            window.ScrollTrigger?.refresh();
        }, 500);
    });
})();
